import React from 'react';
import { Navigate } from 'react-router-dom';
import { useCustomPermissions } from '../hooks/usePermissions';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactElement;
  /**
   * Single permission check
   */
  permission?: { resource: string; action: string };
  /**
   * Multiple permissions - user must have ANY of these
   */
  anyPermissions?: Array<{ resource: string; action: string }>;
  /**
   * Multiple permissions - user must have ALL of these
   */
  allPermissions?: Array<{ resource: string; action: string }>;
  /**
   * Bypass all permission checks for super admin
   * @default true
   */
  allowSuperAdmin?: boolean;
}

/**
 * Route-level permission guard
 * Redirects to /access-denied if user lacks required permissions
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permission,
  anyPermissions,
  allPermissions,
  allowSuperAdmin = true,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isSuperAdmin, loading } =
    useCustomPermissions();

  // Show loading spinner while permissions are being loaded
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Super admin bypass
  if (allowSuperAdmin && isSuperAdmin()) {
    return children;
  }

  // Single permission check
  if (permission) {
    if (!hasPermission(permission.resource, permission.action)) {
      return <Navigate to="/access-denied" replace />;
    }
  }

  // ANY permissions check
  if (anyPermissions && anyPermissions.length > 0) {
    if (!hasAnyPermission(anyPermissions)) {
      return <Navigate to="/access-denied" replace />;
    }
  }

  // ALL permissions check
  if (allPermissions && allPermissions.length > 0) {
    if (!hasAllPermissions(allPermissions)) {
      return <Navigate to="/access-denied" replace />;
    }
  }

  return children;
};
